import { useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  UsersRound,
} from 'lucide-react'

import SEO from '@/components/shared/SEO'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Label } from '@/components/ui/label'
import CareersApplicationForm from '../../components/careers/CareersApplicationForm'
import { applicationConfig, careersPageContent, vacancies } from './careersData'

const DetailList = ({ items }) => {
  if (!items?.length) return null

  return (
    <ul className="text-foreground/80 mt-3 space-y-2 pl-5 text-sm leading-relaxed md:text-base">
      {items.map(item => (
        <li key={item} className="list-disc pl-1">
          {item}
        </li>
      ))}
    </ul>
  )
}

const VacancySection = ({ section }) => (
  <div className="border-border border-t pt-5 first:border-t-0 first:pt-0">
    <h4 className="text-foreground text-base font-black md:text-lg">
      {section.title}
    </h4>

    {section.paragraphs?.map(paragraph => (
      <p
        key={paragraph}
        className="text-foreground/80 mt-3 text-sm leading-relaxed md:text-base"
      >
        {paragraph}
      </p>
    ))}

    <DetailList items={section.items} />

    {section.groups?.map(group => (
      <div key={group.title} className="mt-5">
        <h5 className="text-foreground text-sm font-bold md:text-base">
          {group.title}
        </h5>
        <DetailList items={group.items} />
      </div>
    ))}

    {section.table && (
      <div className="border-border mt-4 overflow-x-auto rounded-md border">
        <table className="text-foreground/80 w-full min-w-[520px] text-left text-sm">
          <thead className="text-foreground bg-gray-100">
            <tr>
              {section.table.headers.map(header => (
                <th key={header} className="px-4 py-3 font-bold">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.table.rows.map(row => (
              <tr key={row.join('-')} className="border-border border-t">
                {row.map(cell => (
                  <td key={cell} className="px-4 py-3">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)

const Careers = () => {
  const formRef = useRef(null)
  const [selectedBrand, setSelectedBrand] = useState('All')
  const [selectedApplication, setSelectedApplication] = useState(null)

  const brandOptions = useMemo(() => {
    const brands = new Set()
    vacancies.forEach(vacancy => {
      vacancy.brands.forEach(brand => brands.add(brand))
    })

    return ['All', ...Array.from(brands).sort()]
  }, [])

  const filteredVacancies = useMemo(() => {
    if (selectedBrand === 'All') return vacancies

    return vacancies.filter(vacancy => vacancy.brands.includes(selectedBrand))
  }, [selectedBrand])

  const handleApplyClick = vacancy => {
    setSelectedApplication({
      position: vacancy.title,
      selectedAt: Date.now(),
    })

    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  return (
    <div className="bg-background text-foreground flex-1">
      <SEO
        title="Careers - Build Your Future With Us | Claims1"
        description="Explore current vacancies and apply for career opportunities across Lloyd James Media Group brands."
        keywords="careers, jobs, vacancies, Lloyd James Media Group, Air Travel Claim, Tanan Tech Limited, Your Claim Matters"
        url="/careers"
        image={applicationConfig.teamImage}
        imageAlt="Professional team working together in a modern office"
        schemaType="WebPage"
        schemaData={{
          mainEntity: {
            '@type': 'ItemList',
            name: 'Current vacancies',
            itemListElement: vacancies.map((vacancy, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: vacancy.title,
              description: `${vacancy.company} - ${vacancy.meta
                .map(item => `${item.label}: ${item.value}`)
                .join(', ')}`,
            })),
          },
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Careers', url: '/careers' },
        ]}
      />

      <section className="bg-gray-100">
        <div className="container mx-auto grid min-h-[68vh] max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:py-20">
          <div>
            <div className="border-primary/30 bg-primary/10 text-primary mb-5 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-bold">
              <BriefcaseBusiness className="size-4" aria-hidden="true" />
              Careers
            </div>
            <h1 className="text-foreground max-w-3xl text-4xl font-black tracking-normal sm:text-5xl lg:text-6xl">
              {careersPageContent.hero.title}
            </h1>
            <div className="text-foreground/80 mt-6 max-w-2xl space-y-4 text-base leading-relaxed sm:text-lg">
              {careersPageContent.hero.intro.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="border-primary/20 overflow-hidden rounded-md border bg-white shadow-xl">
            <img
              src={applicationConfig.teamImage}
              alt="Professional team working together in a modern office"
              className="aspect-[4/3] h-full w-full object-cover lg:aspect-[5/4]"
            />
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-18">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1fr]">
            <div>
              <h2 className="text-foreground text-3xl font-black md:text-4xl">
                Why Work With Us
              </h2>
              <div className="text-foreground/80 mt-5 space-y-4 text-base leading-relaxed">
                {careersPageContent.whyWorkWithUs.map(paragraph => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-foreground text-3xl font-black md:text-4xl">
                Life in our business
              </h2>
              <div className="mt-5 space-y-4">
                {careersPageContent.lifeInBusiness.map(item =>
                  item.type === 'quote' ? (
                    <blockquote
                      key={item.text}
                      className="text-foreground border-l-4 border-[#ff8904] pl-5 text-base leading-relaxed font-semibold"
                    >
                      &ldquo;{item.text}&rdquo;
                    </blockquote>
                  ) : (
                    <div key={item.text} className="flex gap-3">
                      <UsersRound
                        className="mt-1 size-5 shrink-0 text-[#ff8904]"
                        aria-hidden="true"
                      />
                      <p className="text-foreground/80 text-base leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-18" id="current-vacancies">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-foreground text-3xl font-black md:text-4xl">
                Current Vacancies
              </h2>
              <p className="text-foreground/80 mt-3 max-w-2xl text-base leading-relaxed">
                {filteredVacancies.length}{' '}
                {filteredVacancies.length === 1 ? 'opening' : 'openings'}{' '}
                available
                {selectedBrand === 'All' ? '' : ` for ${selectedBrand}`}.
              </p>
            </div>

            <div className="w-full md:w-72">
              <Label
                htmlFor="brand-filter"
                className="text-foreground mb-2 text-sm font-bold"
              >
                Brand
              </Label>
              <select
                id="brand-filter"
                value={selectedBrand}
                onChange={event => setSelectedBrand(event.target.value)}
                className="border-input focus-visible:border-ring focus-visible:ring-ring/50 h-11 w-full rounded-md border bg-white px-3 text-sm outline-none focus-visible:ring-[3px]"
              >
                {brandOptions.map(brand => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="max-h-[35rem] overflow-y-auto pr-2">
            <Accordion type="multiple" className="space-y-4 pb-1">
              {filteredVacancies.map(vacancy => (
                <AccordionItem
                  key={vacancy.id}
                  value={vacancy.id}
                  className="border-border rounded-md border bg-white px-5 shadow-sm"
                >
                  <AccordionTrigger className="cursor-pointer py-5 text-left hover:no-underline">
                    <div className="mr-4">
                      <div className="text-primary flex flex-wrap items-center gap-2 text-xs font-bold uppercase">
                        <Building2 className="size-4" aria-hidden="true" />
                        {vacancy.company}
                      </div>
                      <h3 className="text-foreground mt-2 text-xl font-black md:text-2xl">
                        {vacancy.title}
                      </h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {vacancy.meta.map(item => (
                        <div
                          key={`${vacancy.id}-${item.label}`}
                          className="rounded-md bg-gray-100 p-3"
                        >
                          <div className="text-primary text-xs font-bold uppercase">
                            {item.label}
                          </div>
                          <div className="font-raleway-numbers text-foreground mt-1 text-sm font-bold">
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-6">
                      {vacancy.sections.map(section => (
                        <VacancySection
                          key={`${vacancy.id}-${section.title}`}
                          section={section}
                        />
                      ))}
                    </div>

                    <div className="mt-8 flex justify-start">
                      <Button
                        type="button"
                        size="lg"
                        onClick={() => handleApplyClick(vacancy)}
                        className="h-12 cursor-pointer px-6 text-base font-black"
                      >
                        Apply
                        <ArrowRight className="size-5" aria-hidden="true" />
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CareersApplicationForm
        formRef={formRef}
        selectedApplication={selectedApplication}
      />
    </div>
  )
}

export default Careers
